import { useState, FormEvent, FC } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { Button } from '@/components/ui/button';
import { Loader } from 'components/Loader';
import { useToast } from 'components/useToast';

interface Props {
  billingDetails?: {
    postalCode?: string;
    country?: string;
    email?: string;
  };
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const StripeForm: FC<Props> = ({ billingDetails = {}, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitLoading, setSubmitLoading] = useState(false);
  const { successToast, errorToast } = useToast();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitLoading(true);

    if (!stripe || !elements) {
      setSubmitLoading(false);
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      const errorMessage = submitError.message || 'An error occurred during payment submission';
      errorToast({
        title: 'Payment Error',
        description: errorMessage,
      });
      onError?.(errorMessage);
      setSubmitLoading(false);
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        payment_method_data: {
          billing_details: billingDetails.email ? { email: billingDetails.email } : undefined,
        },
      },
    });

    if (error) {
      const errorMessage = error.message || 'Payment failed. Please try again.';
      errorToast({
        title: 'Payment Failed',
        description: errorMessage,
      });
      onError?.(errorMessage);
    } else {
      successToast({
        title: 'Payment Successful',
        description: 'The payment has been processed successfully. Refreshing page...',
      });
      onSuccess?.();

      // Give the webhook time to process and then reload the page
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }

    setSubmitLoading(false);
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: 'tabs',
    defaultValues: {
      billingDetails: {
        address: {
          postal_code: billingDetails.postalCode || '',
          country: billingDetails.country || 'US',
        },
        email: billingDetails.email || '',
      },
    },
    wallets: { link: 'never' },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <PaymentElement options={paymentElementOptions} />
      </div>

      <Button type="submit" disabled={!stripe || submitLoading} className="w-full">
        {submitLoading ? (
          <>
            <Loader h={4} w={4} color="white" />
            <span className="ml-2">Processing Payment...</span>
          </>
        ) : (
          'Complete Payment'
        )}
      </Button>
    </form>
  );
};
